import { OVERRIDE_SCOPES } from '../../constants/compliance-verdicts';

/*
 * Pure transforms between the override forms and the shapes the compliance verdict endpoints take.
 *
 * Kept dependency-free on purpose: `moment` and `_` are webpack ProvidePlugin globals and are not
 * defined when mocha loads this module, so anything reachable from a test must not rely on them.
 *
 * Unlike the advisory mapper there is no read/write asymmetry to handle - the write body is a
 * strict subset of the read row, same names, same shapes. What this module exists for instead is
 * the granularity invariant: the API rejects a firmware version with no component type with a 422,
 * and deriving the payload from the chosen scope is what makes that combination unreachable.
 */

const trimmed = (value) => {
	return String(value ?? '').trim();
};

/*
 * The payload is derived from `scope`, never from which fields happen to hold a value. That is the
 * whole point: widening the scope after typing a version leaves the old version sitting in the
 * form state, and reading the fields directly would send it and earn a 422. Here a narrower field
 * is simply never read for a wider scope.
 */
export const formValuesToCreatePayload = (values, grantedBy) => {
	const scope = values?.scope ?? OVERRIDE_SCOPES.APPLIANCE;

	const payload = {
		serialNumber: trimmed(values?.serialNumber),
		grantedBy: trimmed(grantedBy),
		reason: trimmed(values?.reason)
	};

	if (scope === OVERRIDE_SCOPES.APPLIANCE) {
		return payload;
	}

	payload.hardwareComponentTypeId = trimmed(values?.hardwareComponentTypeId);

	if (scope === OVERRIDE_SCOPES.COMPONENT_VERSION) {
		payload.firmwareVersion = trimmed(values?.firmwareVersion);
	}

	return payload;
};

/*
 * Granting takes only the audit fields: the scope comes from the verdict being granted, so there is
 * nothing about what is waived for the operator to get wrong.
 */
export const formValuesToGrantPayload = (values, grantedBy) => {
	return {
		grantedBy: trimmed(grantedBy),
		reason: trimmed(values?.reason)
	};
};

export const revokePayload = (revokedBy) => {
	return { revokedBy: trimmed(revokedBy) };
};

/*
 * The inverse of the scope selector: classify a row the API returned. The two nullable columns
 * express all three granularities, so their presence is the whole classification.
 */
export const scopeOf = (verdict) => {
	if (!verdict?.hardwareComponentTypeId) {
		return OVERRIDE_SCOPES.APPLIANCE;
	}

	if (!verdict?.firmwareVersion) {
		return OVERRIDE_SCOPES.COMPONENT_TYPE;
	}

	return OVERRIDE_SCOPES.COMPONENT_VERSION;
};
